import { useMemo, useState } from 'react';
import { Alert, App, Button, Empty, Form, Input, Modal, Space, Tag, Typography } from 'antd';
import { apiErrorCode, apiErrorMessage } from '@/api/client';
import { AI_EXTRACT_MAX_CHARS, aiExtractPayee, type AiExtractResult } from '@/api/payees';

const { Paragraph, Text, Title } = Typography;

const REJECT_REASON: Record<AiExtractResult['rejected'][number]['reason'], string> = {
  not_an_option: '不在可选项里',
  pattern_mismatch: '格式不符合要求',
};

/**
 * 收款人 AI 录入工作台：左侧原文始终保留，右侧按当次动态 schema 展示完整表单。
 * 用户确认后才回填主表单；联网补齐的值逐字段标明来源，不能和原文识别混在一起。
 */
export default function AiQuickFillModal({
  open,
  onClose,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  onApply: (result: AiExtractResult) => void;
}) {
  const { message } = App.useApp();
  const [text, setText] = useState('');
  const [parsedText, setParsedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AiExtractResult | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);

  const dirty = result !== null && text.trim() !== parsedText;
  const missingPaths = useMemo(
    () => new Set((result?.missing ?? []).map((field) => field.path)),
    [result],
  );

  function reset() {
    setText('');
    setParsedText('');
    setLoading(false);
    setResult(null);
    setNotConfigured(false);
  }

  async function parse() {
    const input = text.trim();
    if (!input) return;
    setLoading(true);
    setNotConfigured(false);
    try {
      const next = await aiExtractPayee(text);
      setResult(next);
      setParsedText(input);
    } catch (err) {
      if (apiErrorCode(err) === 'AI_NOT_CONFIGURED') setNotConfigured(true);
      else message.error(apiErrorMessage(err, 'AI 解析失败，请稍后重试'));
    } finally {
      setLoading(false);
    }
  }

  const fields = result?.fields ?? [];

  return (
    <Modal
      open={open}
      title="AI 快速录入收款人"
      width={1200}
      onCancel={onClose}
      afterClose={reset}
      mask={{ closable: !loading }}
      footer={
        <Space>
          <Button onClick={onClose} disabled={loading}>
            取消
          </Button>
          <Button
            type="primary"
            disabled={!result || result.filled.length === 0 || dirty || loading}
            onClick={() => result && onApply(result)}
          >
            确认并填入表单
          </Button>
        </Space>
      }
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(430px, 100%), 1fr))',
          gap: 24,
          minHeight: 520,
        }}
      >
        <section style={{ minWidth: 0 }}>
          <Title level={5} style={{ marginTop: 0 }}>
            1. 粘贴任意信息
          </Title>
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            聊天记录、邮件、发票抬头、银行信息都可以整段粘贴，多余的话不用删。原文会一直保留，
            方便和右侧逐项核对。
          </Paragraph>
          <Input.TextArea
            value={text}
            onChange={(event) => setText(event.target.value)}
            disabled={loading}
            autoSize={{ minRows: 16, maxRows: 24 }}
            maxLength={AI_EXTRACT_MAX_CHARS}
            showCount
            placeholder={
              '例如：\nAccount holder: John Walker\nSWIFT: CHASUS33\nAccount No.: 50001121\n412 5th Avenue, Seattle, WA 98104'
            }
          />
          <Button
            type="primary"
            block
            style={{ marginTop: 12 }}
            loading={loading}
            disabled={!text.trim() || notConfigured}
            onClick={() => void parse()}
          >
            {result ? '重新解析并自动补齐' : '解析并自动补齐'}
          </Button>

          {dirty && (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              title="左侧内容已修改"
              description="请重新解析后再确认，避免右侧仍是旧结果。"
            />
          )}
          {notConfigured ? (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              title="本环境未启用 AI 快速录入"
              description="服务端还没有配置 AI 服务。你仍然可以关闭弹窗后手动填写。"
            />
          ) : (
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              title="内容会发送给第三方 AI 服务"
              description="模型会解析原文，并可能联网搜索邮编等公开地址信息。原文和结果不会保存；请在确认前逐项核对。"
            />
          )}
        </section>

        <section
          style={{
            minWidth: 0,
            borderInlineStart: '1px solid var(--ant-color-border-secondary)',
            paddingInlineStart: 24,
            maxHeight: '68vh',
            overflowY: 'auto',
          }}
        >
          <Title level={5} style={{ marginTop: 0 }}>
            2. 核对解析结果
          </Title>
          {!result ? (
            <Empty description="解析后，完整表单会显示在这里" style={{ paddingTop: 120 }} />
          ) : (
            <>
              {result.filled.length === 0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  title="没有识别出可用的字段"
                  description="请检查左侧内容，补充银行账户信息后重新解析。"
                />
              )}
              {result.searchStatus === 'failed' && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  title="公开信息自动搜索暂时失败"
                  description="已经从原文识别出的内容仍可使用；缺少的字段请手动补充。"
                />
              )}
              <Form layout="vertical" component="div">
                {fields.map((field) => {
                  const value = result.values[field.path] ?? '';
                  const source = result.sources?.[field.path];
                  const option = field.options.find((item) => item.value === value);
                  const displayValue = option ? `${option.label} (${option.value})` : value;
                  const missing = missingPaths.has(field.path);
                  return (
                    <Form.Item
                      key={field.path}
                      label={field.label}
                      required={field.required}
                      validateStatus={missing ? 'warning' : undefined}
                      help={
                        source === 'ai_search' ? (
                          <Text type="warning" style={{ fontSize: 12 }}>
                            <Tag color="gold" style={{ marginInlineEnd: 6 }}>
                              AI 搜索补齐
                            </Tag>
                            通过联网搜索补充，请重点核对
                          </Text>
                        ) : missing ? (
                          <Text type="warning" style={{ fontSize: 12 }}>
                            未能从原文或公开信息中确定，请在主表单手动补充
                          </Text>
                        ) : field.description ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {field.description}
                          </Text>
                        ) : undefined
                      }
                    >
                      <Input
                        value={displayValue}
                        readOnly
                        status={missing ? 'warning' : undefined}
                        placeholder={field.example || '未识别'}
                      />
                    </Form.Item>
                  );
                })}
              </Form>

              {fields.length === 0 && (
                <Alert
                  type="warning"
                  showIcon
                  title="还无法确定完整表单"
                  description="请在左侧补充收款地区、币种或账户信息后重新解析。"
                />
              )}

              {result.rejected.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Text strong>未采用的信息</Text>
                  <Space direction="vertical" size={4} style={{ width: '100%', marginTop: 8 }}>
                    {result.rejected.map((field) => (
                      <Alert
                        key={`${field.path}-${field.value}`}
                        type="warning"
                        title={`${field.label}：${field.value}`}
                        description={REJECT_REASON[field.reason]}
                      />
                    ))}
                  </Space>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </Modal>
  );
}
